import * as Cesium from "cesium";
import {
  ICenterLevelParams,
  MapServerType,
  IDrawOverlaysAddParams,
  IDrawOverlaysDeleteParams,
  IResult,
  IOverlaysAddParams,
  IOverlaysDeleteParams,
  IDriveModelParams,
  IDriveStatusParams,
  IDriveModelDeleteParams,
  IDriveFocusParams,
  IModelParams,
  IModelRemoveParams,
  IDriveRouteParams,
} from "@/types/cesiumMap";
import * as Utils from "./cesiumUtils";
import DrawOverlays from "./widgets/DrawOverlays/cesium/DrawOverlays";
import Overlays from "./widgets/Overlays/cesium/OverlaysCesium";
// import Drive from "./Drive/Drive";
// import Model from "./Model/Model";
// import RouteDrive from "./Drive/RouteDrive";

export default class MapApp {
  private viewer!: any;

  private _mouseOverEntity: any; //鼠标当前盖住的最上层的entity

  public mapClick!: (event: any) => void;
  public markerClick!: (event: any) => void;
  public markerMouseMove!: (event: any) => void;
  public markerMouseIn!: (event: any) => void;
  public markerMouseOut!: (event: any) => void;

  /**
   * 地图初始化
   * 加载依赖文件，解析配置参数，初始化viewer对象
   */
  public async init(elementId: string, mapConfig: any): Promise<any> {
    Cesium.Ion.defaultAccessToken = "do not use Ion";
    Cesium.Ion.defaultServer = "do not use Ion";

    //加载依赖文件
    const apiUrl: string = mapConfig.apiUrl || "/libs/Cesium/Cesium.js";
    await Utils.loadScripts([apiUrl]).then(() => {
      console.log("Cesium.js loaded");
    });
    const cssUrl = [
      apiUrl.substring(0, apiUrl.lastIndexOf("/")) + "/Widgets/widgets.css",
    ];
    await Utils.loadCSS(cssUrl).then(() => {
      console.log("widgets.css loaded!");
    });

    //初始化viewer
    const viewer = new Cesium.Viewer(elementId, {
      shouldAnimate: true, // 时间开始流逝
      imageryProvider: this._baseLayer(mapConfig),
      terrainProvider: this._terrainLayer(mapConfig),
      sceneMode: this._sceneMode(mapConfig), //场景模式：3d地球 | 2.5d地图 |2d
      // mapProjection: new Cesium.WebMercatorProjection(), //地图投影
      animation: false, //动画时钟 组件
      timeline: false, //时间线
      infoBox: false, //选中要素后显示的信息框
      baseLayerPicker: false, //底图图层选择按钮
      fullscreenButton: false, //全屏按钮
      vrButton: false, //vr按钮
      geocoder: false, //地理位置查找工具
      homeButton: false, //home按钮
      sceneModePicker: false, //场景模式选择
      selectionIndicator: true, //选中要素后标识的指示器
      navigationHelpButton: false, //导航帮助按钮
      navigationInstructionsInitiallyVisible: false, //导航帮助按钮是否默认展开
    });
    this.viewer = viewer;
    this._addModel(mapConfig, viewer);
    //取消地图点击事件
    viewer.cesiumWidget.screenSpaceEventHandler.removeInputAction(
      Cesium.ScreenSpaceEventType.LEFT_CLICK
    );
    // viewer.imageryLayers.removeAll();
    // console.log(viewer.imageryLayers);

    // Inspector信息框
    // viewer.extend(Cesium.viewerCesiumInspectorMixin);

    //foucus on canvas
    const canvas = viewer.canvas;
    canvas.addEventListener("click", () => {
      canvas.focus();
    });
    canvas.focus();

    //set camera postion
    const camera = viewer.camera;
    camera.setView({
      destination: this._cameraPosition(mapConfig),
    });

    //set max zoom
    // const controller = viewer.scene.screenSpaceCameraController;
    // controller.maximumZoomDistance = Utils.zoomToAltitude();

    //handle map click event
    this._handleMapClick();

    //handle overlay left click
    this._registerLeftClick(this.markerClick);
    //handle overlay mouse move
    this._registerMousemove(this.markerMouseMove);
    //handle overlay mouse in and out
    this._registerMouseInOut(this.markerMouseIn, this.markerMouseOut);

    //return resolve
    return new Promise<void>((resolve) => {
      resolve();
    });
  }
  //加载模型
  private _addModel(config: any, viewer: any): void {
    if (config.customLayers.length > 0) {
      config.customLayers.forEach((item: any) => {
        if (item.type == "model") {
          let position = Cesium.Cartesian3.fromDegrees(
            121.2362522,
            31.2852682,
            0
          );
          let hpr = new Cesium.HeadingPitchRoll(
            Cesium.Math.toRadians(90),
            0,
            0
          );
          let orientation = Cesium.Transforms.headingPitchRollQuaternion(
            position,
            hpr
          );
          viewer.entities.add({
            id: "buildId",
            name: item.url,
            position: position,
            orientation: orientation,
            model: {
              uri: item.url,
              //不管缩放如何，模型的最小最小像素大小。
              minimumPixelSize: 120,
              //模型的最大比例尺大小。 minimumPixelSize的上限。
              maximumScale: 20000,
              scale: 0.8547,
            },
          });
        }
      });
    }
  }

  //get base layer provider
  private _baseLayer(config: any): any {
    const layer = config.baseLayer;
    if (layer.type == MapServerType.ARCGIS) {
      const imageryProvider = new Cesium.ArcGisMapServerImageryProvider({
        url: layer.url,
      });
      // imageryProvider.readyPromise.then(() => {
      //   console.log(imageryProvider.maximumLevel, imageryProvider.minimumLevel);
      // });
      return imageryProvider;
    }
  }

  //get terrain provider
  private _terrainLayer(config: any): any {
    const layer = config.terrainLayer;
    if (!config.terrainLayer) {
      return undefined;
    }
    if (layer.type == MapServerType.CESIUM) {
      return new Cesium.CesiumTerrainProvider({
        url: layer.url,
      });
    } else if (layer.type == MapServerType.ARCGIS) {
      return new Cesium.ArcGISTiledElevationTerrainProvider({
        url: layer.url,
      });
    }
  }

  //get scene mode ,默认2D
  private _sceneMode(config: any): any {
    const sm = config.options?.mapMode;
    if (!sm) {
      return Cesium.SceneMode.SCENE2D;
    }
    if (sm == "2.5D") {
      return Cesium.SceneMode.COLUMBUS_VIEW;
    } else if (sm == "3D") {
      return Cesium.SceneMode.SCENE3D;
    } else {
      return Cesium.SceneMode.SCENE2D;
    }
  }

  //从配置中获取初始视角位置
  private _cameraPosition(config: any): any {
    //get "level"
    let altitude = 100000;
    if (config.options?.level) {
      const level = config.options?.level as number;
      altitude = Utils.zoomToAltitude(level);
    }
    //get "center"
    if (config.options?.center) {
      const center = config.options?.center as number[];
      if (center.length < 2) {
        return;
      }
      if (center.length == 2) {
        return Cesium.Cartesian3.fromDegrees(center[0], center[1], altitude);
      }
      if (center.length == 3) {
        return Cesium.Cartesian3.fromDegrees(center[0], center[1], center[2]);
      }
    }
  }

  //处理地图点击事件
  private _handleMapClick() {
    //响应地图左击事件
    const handler = new Cesium.ScreenSpaceEventHandler(this.viewer.canvas);
    handler.setInputAction((event: any) => {
      const pickedPosition = Utils.pickPositionFromScreen(
        event.position,
        this.viewer
      );
      if (Cesium.defined(pickedPosition)) {
        //触发自定义的地图点击事件
        const lnglat = Cesium.Cartographic.fromCartesian(pickedPosition);
        this.mapClick({
          Cartesian3: {
            x: pickedPosition.x,
            y: pickedPosition.y,
            z: pickedPosition.z,
          },
          LngLat: {
            longitude: Cesium.Math.toDegrees(lnglat.longitude),
            latitude: Cesium.Math.toDegrees(lnglat.latitude),
            height: Cesium.Math.toDegrees(lnglat.height),
          },
        });
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  }

  //注册鼠标左击击中entity和primitive的事件
  private _registerLeftClick(call: any): any {
    const eventHandler = new Cesium.ScreenSpaceEventHandler(
      this.viewer.scene.canvas
    );
    eventHandler.setInputAction((event: any) => {
      const feature = this.viewer.scene.pick(event.position);
      if (Cesium.defined(feature)) {
        //解析entity的几何数据和属性数据回调
        if (feature.id instanceof Cesium.Entity) {
          const entity = feature.id;
          call({
            id: entity.id,
            type: entity.name,
            geometry: Utils.parseEntityGeometry(entity, this.viewer.clock),
            attributes: Utils.copyObject(entity.attributes),
            sceenPoint: Utils.copyObject(event.position),
          });
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    return eventHandler;
  }

  //注册鼠标移动事件
  //@notice 这个事件注册后对3dtiles模型数据拖动的帧率和流畅有影响
  private _registerMousemove(call: any): any {
    const eventHandler = new Cesium.ScreenSpaceEventHandler(
      this.viewer.scene.canvas
    );
    eventHandler.setInputAction((event: any) => {
      const feature = this.viewer.scene.pick(event.endPosition);
      if (Cesium.defined(feature) && Cesium.defined(feature.id)) {
        //解析entity的几何数据和属性数据回调
        if (feature.id instanceof Cesium.Entity) {
          const entity = feature.id;
          call({
            id: entity.id,
            type: entity.name,
            geometry: Utils.parseEntityGeometry(entity, this.viewer.clock),
            attributes: Utils.copyObject(entity.attributes),
            sceenPoint: Utils.copyObject(event.startPosition),
          });
        }
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
    return eventHandler;
  }

  //注册鼠标移入移出事件
  //@notice 该事件对entity有效
  private _registerMouseInOut(InCall: any, OutCall: any): any {
    const eventHandler = new Cesium.ScreenSpaceEventHandler(
      this.viewer.scene.canvas
    );
    eventHandler.setInputAction((event: any) => {
      const feature = this.viewer.scene.pick(event.endPosition);
      //解析上次选中的entity数据
      let callbackData_last;
      if (
        this._mouseOverEntity &&
        this._mouseOverEntity instanceof Cesium.Entity
      ) {
        callbackData_last = {
          id: this._mouseOverEntity.id,
          type: this._mouseOverEntity.name,
          geometry: Utils.parseEntityGeometry(
            this._mouseOverEntity,
            this.viewer.clock
          ),
          attributes: Utils.copyObject(
            (this._mouseOverEntity as any).attributes
          ),
          sceenPoint: Utils.copyObject(event.startPosition),
        };
      }
      //判断本次是否为entity
      if (Cesium.defined(feature) && Cesium.defined(feature.id)) {
        //解析entity的几何数据和属性数据回调
        if (feature.id instanceof Cesium.Entity) {
          const entity = feature.id;
          const callbackData = {
            id: entity.id,
            type: entity.name,
            geometry: Utils.parseEntityGeometry(entity, this.viewer.clock),
            attributes: Utils.copyObject(entity.attributes),
            sceenPoint: Utils.copyObject(event.startPosition),
          };
          //对比本次和上次被选中的entity，判断是否移入移出
          if (this._mouseOverEntity) {
            //上次选中了entity，这次又选中，若不同，则是移入移出，若相同则是do nothing
            if (this._mouseOverEntity != entity) {
              OutCall(callbackData_last);
              this._mouseOverEntity = entity;
              InCall(callbackData);
            }
          } else {
            //上次没选中entity，第一次选中entity则是移入
            this._mouseOverEntity = entity;
            InCall(callbackData);
          }
        } else {
          //当选中的不是entity时，则判断是否移出（上一次有选中则是移出）
          if (Cesium.defined(this._mouseOverEntity)) {
            OutCall(callbackData_last);
            this._mouseOverEntity = undefined;
          }
        }
      } else {
        //当未选中entity时，则判断是否移出（上一次有选中则是移出）
        if (Cesium.defined(this._mouseOverEntity)) {
          OutCall(callbackData_last);
          this._mouseOverEntity = undefined;
        }
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
    return eventHandler;
  }

  //@todo 获取底图的最小缩放图层

  //-----------------------------对外接口实现----------------------------//

  /**
   * setMapCenterAndLevel
   * 设置地图视角中心点和层级
   */
  public setMapCenterAndLevel(params: ICenterLevelParams) {
    this.viewer.camera.flyTo({
      destination: this._cameraPosition({
        options: params,
      }),
    });
  }

  /**
   * startDrawOverlays
   * 开始绘制地图覆盖物，包括点、线、面
   */
  public async startDrawOverlays(
    params: IDrawOverlaysAddParams
  ): Promise<IResult> {
    const draw = DrawOverlays.GetInstance(this.viewer);
    return draw.startDrawOverlays(params);
  }

  // /**
  //  * stopDrawOverlays
  //  * 停止绘制
  //  */
  // public async stopDrawOverlays(): Promise<IResult> {
  //   const draw = DrawOverlays.GetInstance(this.viewer);
  //   return draw.stopDrawOverlays();
  // }

  /**
   * deleteDrawOverlays
   * 删除绘制物
   */
  public async deleteDrawOverlays(
    params: IDrawOverlaysDeleteParams
  ): Promise<IResult> {
    const draw = DrawOverlays.GetInstance(this.viewer);
    return draw.deleteDrawOverlays(params);
  }

  /**
   * addOverlays
   * 添加点、线、面覆盖物到地图上
   */
  public async addOverlays(params: IOverlaysAddParams): Promise<IResult> {
    const overlays = Overlays.GetInstance(this.viewer);
    return overlays.addOverlays(params);
  }

  /**
   * deleteOverlays
   * 删除地图上指定的点、线、面覆盖物
   */
  public async deleteOverlays(params: IOverlaysDeleteParams): Promise<IResult> {
    const overlays = Overlays.GetInstance(this.viewer);
    return overlays.deleteOverlays(params);
  }

  // /**
  //  * loadDriveModel
  //  * 加载实时驾驶模型
  //  */
  // public async loadDriveModel(params: IDriveModelParams): Promise<IResult> {
  //   const drive = Drive.GetInstance(this.viewer);
  //   return drive.loadModel(params);
  // }

  // /**
  //  * changeDriveStatus
  //  * 改变车辆模型的实时行驶状态
  //  */
  // public changeDriveStatus(params: IDriveStatusParams): void {
  //   const drive = Drive.GetInstance(this.viewer);
  //   return drive.changeDriveStatus(params);
  // }

  // /**
  //  * removeDriveModel
  //  * 从图上移除模型
  //  */
  // public removeDriveModel(params: IDriveModelDeleteParams): IResult {
  //   const drive = Drive.GetInstance(this.viewer);
  //   return drive.removeModel(params);
  // }

  // /**
  //  * focusDriveModel
  //  * 视角聚焦于指定模型
  //  */
  // public focusDriveModel(params: IDriveFocusParams): void {
  //   const drive = Drive.GetInstance(this.viewer);
  //   return drive.focusModel(params);
  // }

  // /**
  //  * cancelFocusDriveModel
  //  * 取消聚焦实时行驶模型
  //  */
  // public cancelFocusDriveModel(): void {
  //   const drive = Drive.GetInstance(this.viewer);
  //   return drive.cancelFocusModel();
  // }

  // /**
  //  * loadRouteDriveModel
  //  * 加载按照路线行驶的模型
  //  */
  // public loadRouteDriveModel(p: IDriveModelParams): Promise<IResult> {
  //   const routeDrive = RouteDrive.GetInstance(this.viewer);
  //   return routeDrive.loadModel(p);
  // }

  // /**
  //  * removeRouteDriveModel
  //  * 移除按照路线行驶的模型
  //  */
  // public removeRouteDriveModel(p: IModelRemoveParams): void {
  //   const routeDrive = RouteDrive.GetInstance(this.viewer);
  //   return routeDrive.removeModel(p);
  // }

  // /**
  //  * focusRouteDrive
  //  * 聚焦按照路线行驶的模型
  //  */
  // public focusRouteDrive(p: IDriveFocusParams): void {
  //   const routeDrive = RouteDrive.GetInstance(this.viewer);
  //   return routeDrive.focusModel(p);
  // }

  // /**
  //  * cancelFocusRouteDrive
  //  * 取消聚焦
  //  */
  // public cancelFocusRouteDrive(): void {
  //   const routeDrive = RouteDrive.GetInstance(this.viewer);
  //   return routeDrive.cancelFocusModel();
  // }
  // /**
  //  * driveOnRoute
  //  *
  //  */
  // public driveOnRoute(p: IDriveRouteParams): Promise<IResult> {
  //   const routeDrive = RouteDrive.GetInstance(this.viewer);
  //   return routeDrive.driveOnRoute(p);
  // }

  // /**
  //  * load3Dtiles
  //  * 加载3dtiles数据
  //  */
  // public load3Dtiles(params: IModelParams): Promise<IResult> {
  //   const model = Model.GetInstance(this.viewer);
  //   return model.load3Dtiles(params);
  // }

  // /**
  //  * remove3Dtiles
  //  * 移除3dtile模型
  //  */
  // public remove3Dtiles(params: IModelRemoveParams) {
  //   const model = Model.GetInstance(this.viewer);
  //   return model.remove3Dtiles(params);
  // }

  // /**
  //  * loadGLTF
  //  */
  // public loadGLTF(params: IModelParams): Promise<IResult> {
  //   const model = Model.GetInstance(this.viewer);
  //   return model.loadModel(params);
  // }

  // /**
  //  * removeGLTF
  //  */
  // public removeGLTF(params: IModelRemoveParams) {
  //   const model = Model.GetInstance(this.viewer);
  //   return model.removeModel(params);
  // }
}
